import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Palette, Type as TypeIcon, Search, FileText, Plus, Trash2, Copy as CopyIcon, ExternalLink, ChevronUp, ChevronDown } from "lucide-react";

import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FONT_LIBRARY } from "@/lib/fonts";

/**
 * Phase 3 Site Settings modal — Branding, Typography, SEO, Pages.
 *
 * The modal reuses existing backend endpoints:
 *  - GET/PUT /api/business/settings (typography, page_bg_color, language)
 *  - PATCH /api/business/website (brand_primary/accent/neutral, seo_title,
 *    meta_description, social_image_url)
 *  - GET/PUT/DELETE/POST /api/business/pages* (page CRUD)
 */
export default function SiteSettingsModal({ bizId, biz, publicPayload, onClose, refresh }) {
  const [tab, setTab] = useState("branding");
  const [settings, setSettings] = useState(null); // GET /business/settings
  const [dirty, setDirty] = useState(false);

  // Refresh parent once, right before we close the modal.
  const closeAndRefresh = async () => {
    if (dirty) {
      try { await refresh?.(); } catch (_) { /* non-fatal */ }
    }
    onClose();
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/business/settings?business_id=${bizId}`);
        if (mounted) setSettings(data);
      } catch (e) {
        toast.error("Couldn't load settings");
      }
    })();
    return () => { mounted = false; };
  }, [bizId]);

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
      onClick={closeAndRefresh}
      data-testid="site-settings-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col border-2 border-black"
        onClick={(e) => e.stopPropagation()}
        data-testid="site-settings-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-black bg-emerald-50">
          <div>
            <h2 className="text-lg font-black">Site Settings</h2>
            <p className="text-[11px] text-gray-600">Site-wide branding, typography, SEO, and pages.</p>
          </div>
          <button
            onClick={closeAndRefresh}
            className="w-8 h-8 rounded-full bg-white border-2 border-black hover:bg-gray-50 flex items-center justify-center"
            data-testid="site-settings-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex border-b border-gray-200 bg-white">
          <TabBtn active={tab === "branding"} onClick={() => setTab("branding")} icon={Palette} label="Branding" testId="site-settings-tab-branding" />
          <TabBtn active={tab === "typography"} onClick={() => setTab("typography")} icon={TypeIcon} label="Typography" testId="site-settings-tab-typography" />
          <TabBtn active={tab === "seo"} onClick={() => setTab("seo")} icon={Search} label="SEO" testId="site-settings-tab-seo" />
          <TabBtn active={tab === "pages"} onClick={() => setTab("pages")} icon={FileText} label="Pages" testId="site-settings-tab-pages" />
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === "branding" && <BrandingTab bizId={bizId} publicPayload={publicPayload} onSaved={() => setDirty(true)} />}
          {tab === "typography" && <TypographyTab bizId={bizId} settings={settings} setSettings={setSettings} onSaved={() => setDirty(true)} />}
          {tab === "seo" && <SeoTab bizId={bizId} publicPayload={publicPayload} onSaved={() => setDirty(true)} />}
          {tab === "pages" && <PagesTab bizId={bizId} biz={biz} refresh={refresh} onSaved={() => setDirty(true)} />}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 ${active ? "border-emerald-500 text-emerald-700 bg-emerald-50/40" : "border-transparent text-gray-600 hover:text-black"}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Branding                                                                   */
/* -------------------------------------------------------------------------- */

function BrandingTab({ bizId, publicPayload, onSaved }) {
  const wc = publicPayload?.website_content || {};
  const [primary, setPrimary] = useState(wc.brand_primary || "");
  const [accent, setAccent]   = useState(wc.brand_accent || "");
  const [neutral, setNeutral] = useState(wc.brand_neutral || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/business/website?business_id=${bizId}`, {
        brand_primary: primary,
        brand_accent: accent,
        brand_neutral: neutral,
      });
      toast.success("Branding saved.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save branding");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="branding-tab">
      <p className="text-xs text-gray-600 leading-relaxed">
        Set your primary, accent, and neutral colors. These flow into the
        header CTA, hero button, and section accents automatically.
      </p>
      <ColorPickerRow label="Primary color" value={primary} onChange={setPrimary} testId="branding-primary" />
      <ColorPickerRow label="Accent color" value={accent} onChange={setAccent} testId="branding-accent" />
      <ColorPickerRow label="Neutral color" value={neutral} onChange={setNeutral} testId="branding-neutral" />
      <div className="grid grid-cols-3 gap-2 mt-4">
        {[primary, accent, neutral].filter(Boolean).map((c, i) => (
          <div key={i} className="rounded-md border border-gray-200 overflow-hidden">
            <div className="h-14" style={{ background: c }} />
            <div className="px-2 py-1 text-[11px] font-mono bg-gray-50">{c}</div>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="branding-save">
        {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
        Save Branding
      </Button>
    </div>
  );
}

function ColorPickerRow({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-3">
      <label className="w-32 text-xs font-bold text-gray-700">{label}</label>
      <input
        type="color"
        value={value || "#000000"}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded border border-gray-200 cursor-pointer"
        data-testid={`${testId}-swatch`}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        className="flex-1 font-mono text-xs"
        data-testid={`${testId}-input`}
      />
      {value && (
        <button onClick={() => onChange("")} className="text-[11px] text-gray-500 hover:text-black underline" data-testid={`${testId}-clear`}>
          Clear
        </button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Typography                                                                 */
/* -------------------------------------------------------------------------- */

function TypographyTab({ bizId, settings, setSettings, onSaved }) {
  const typo = settings?.typography || {};
  const [headingFont, setHeadingFont] = useState(typo.heading_font || "");
  const [bodyFont, setBodyFont]       = useState(typo.body_font || "");
  const [buttonFont, setButtonFont]   = useState(typo.button_font || "");
  const [headingWeight, setHeadingWeight] = useState(typo.heading_weight || "");
  const [bodyWeight, setBodyWeight]       = useState(typo.body_weight || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.typography) {
      const t = settings.typography;
      setHeadingFont(t.heading_font || "");
      setBodyFont(t.body_font || "");
      setButtonFont(t.button_font || "");
      setHeadingWeight(t.heading_weight || "");
      setBodyWeight(t.body_weight || "");
    }
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const nextTypo = {
        ...typo,
        heading_font: headingFont || undefined,
        body_font: bodyFont || undefined,
        button_font: buttonFont || undefined,
        heading_weight: headingWeight || undefined,
        body_weight: bodyWeight || undefined,
      };
      Object.keys(nextTypo).forEach((k) => (nextTypo[k] === undefined || nextTypo[k] === "") && delete nextTypo[k]);
      const { data } = await api.put(`/business/settings?business_id=${bizId}`, { typography: nextTypo });
      setSettings(data);
      toast.success("Typography saved.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save typography");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <SpinnerBox />;

  return (
    <div className="space-y-4" data-testid="typography-tab">
      <p className="text-xs text-gray-600 leading-relaxed">
        Choose fonts and weights for your headings, body text, and buttons.
      </p>
      <FontSelect label="Heading font" value={headingFont} onChange={setHeadingFont} testId="typography-heading-font" />
      <FontSelect label="Body font" value={bodyFont} onChange={setBodyFont} testId="typography-body-font" />
      <FontSelect label="Button font" value={buttonFont} onChange={setButtonFont} testId="typography-button-font" />
      <WeightSelect label="Heading weight" value={headingWeight} onChange={setHeadingWeight} testId="typography-heading-weight" />
      <WeightSelect label="Body weight" value={bodyWeight} onChange={setBodyWeight} testId="typography-body-weight" />
      <Button onClick={save} disabled={saving} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="typography-save">
        {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
        Save Typography
      </Button>
    </div>
  );
}

function FontSelect({ label, value, onChange, testId }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] items-center gap-3">
      <label className="text-xs font-bold text-gray-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-200 rounded-md px-2 py-2 text-sm"
        data-testid={testId}
      >
        <option value="">Default</option>
        {FONT_LIBRARY.map((f) => (
          <option key={f.id} value={f.id}>{f.label} ({f.category})</option>
        ))}
      </select>
    </div>
  );
}

function WeightSelect({ label, value, onChange, testId }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] items-center gap-3">
      <label className="text-xs font-bold text-gray-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-200 rounded-md px-2 py-2 text-sm"
        data-testid={testId}
      >
        <option value="">Default</option>
        <option value="400">Regular (400)</option>
        <option value="500">Medium (500)</option>
        <option value="600">Semibold (600)</option>
        <option value="700">Bold (700)</option>
        <option value="800">Extrabold (800)</option>
      </select>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* SEO                                                                        */
/* -------------------------------------------------------------------------- */

function SeoTab({ bizId, publicPayload, onSaved }) {
  const wc = publicPayload?.website_content || {};
  const [seoTitle, setSeoTitle]     = useState(wc.seo_title || "");
  const [metaDescription, setMetaDescription] = useState(wc.meta_description || "");
  const [socialImageUrl, setSocialImageUrl]   = useState(wc.social_image_url || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/business/website?business_id=${bizId}`, {
        seo_title: seoTitle,
        meta_description: metaDescription,
        social_image_url: socialImageUrl,
      });
      toast.success("SEO saved.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save SEO");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="seo-tab">
      <p className="text-xs text-gray-600 leading-relaxed">
        These settings are used by Google and social platforms when your site is shared or indexed.
      </p>
      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1.5">Site title</label>
        <Input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} maxLength={70} placeholder="Site title (max 60 chars for Google)" data-testid="seo-title" />
        <div className="text-[11px] text-gray-500 mt-1">{seoTitle.length}/60 characters</div>
      </div>
      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1.5">Meta description</label>
        <Textarea rows={3} value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} maxLength={200} placeholder="Short site description (150-160 chars)" data-testid="seo-description" />
        <div className="text-[11px] text-gray-500 mt-1">{metaDescription.length}/160 characters</div>
      </div>
      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1.5">Social share image URL</label>
        <Input value={socialImageUrl} onChange={(e) => setSocialImageUrl(e.target.value)} placeholder="https://…" data-testid="seo-social-image" />
        <p className="text-[11px] text-gray-500 mt-1">Recommended: 1200×630 for Twitter/Facebook/LinkedIn.</p>
        {socialImageUrl && (
          <img src={socialImageUrl} alt="social preview" className="mt-2 w-full max-w-md rounded border border-gray-200" data-testid="seo-social-preview" />
        )}
      </div>
      <Button onClick={save} disabled={saving} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="seo-save">
        {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
        Save SEO
      </Button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Pages                                                                      */
/*                                                                            */
/* Iter 73 — this is now the ONLY page-management surface. The old            */
/* dashboard Pages tab and the section-block editor are gone; page content    */
/* lives in `website_content` and renders through SRC templates. All the      */
/* actions here are metadata-only:                                            */
/*   - Add page: title + slug + nav label + SEO. No AI content generation.    */
/*   - Edit page: title, nav_label, slug, SEO, hide-from-nav.                 */
/*   - Reorder: drag arrows adjust `order`; home is pinned first.             */
/*   - Delete: removes the metadata row. Home cannot be deleted.              */
/* Duplicate is intentionally gone — it copied `sections[]` and no longer     */
/* makes sense under the new architecture.                                    */
/* -------------------------------------------------------------------------- */

function PagesTab({ bizId, biz, refresh }) {
  const [pages, setPages] = useState(biz?.pages || []);
  const [busy, setBusy]   = useState(false);
  const [addForm, setAddForm] = useState({ title: "" });
  const [editing, setEditing] = useState(null); // page_id
  const [editForm, setEditForm] = useState({ title: "", slug: "", nav_label: "", seo_title: "", meta_description: "", hide_from_nav: false });

  useEffect(() => {
    setPages(biz?.pages || []);
  }, [biz]);

  const reloadPages = async () => {
    const { data } = await api.get(`/business/pages?business_id=${bizId}`);
    const arr = Array.isArray(data) ? data : (data?.pages || []);
    setPages(arr);
    await refresh?.();
  };

  const addPage = async () => {
    if (!addForm.title.trim()) return toast.error("Enter a page name");
    setBusy(true);
    try {
      // Metadata-only Add — the endpoint discards any legacy `prompt`
      // and `kind_hint` fields, so we just send the title.
      await api.post(`/business/pages/add?business_id=${bizId}`, { title: addForm.title.trim() });
      setAddForm({ title: "" });
      await reloadPages();
      toast.success("Page added.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add page");
    } finally {
      setBusy(false);
    }
  };

  const deletePage = async (p) => {
    if (p.is_home) return toast.error("Home page can't be deleted.");
    if (!window.confirm(`Delete page "${p.title || p.slug}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/business/pages/${p.page_id}?business_id=${bizId}`);
      await reloadPages();
      toast.success("Page deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete page");
    } finally {
      setBusy(false);
    }
  };

  const movePage = async (p, direction) => {
    // Home is always index 0; other pages can be reordered.
    if (p.is_home) return;
    const nonHome = pages.filter((x) => !x.is_home);
    const idx = nonHome.findIndex((x) => x.page_id === p.page_id);
    const target = idx + direction;
    if (target < 0 || target >= nonHome.length) return;
    const [moved] = nonHome.splice(idx, 1);
    nonHome.splice(target, 0, moved);
    setBusy(true);
    try {
      await api.post(`/business/pages/reorder?business_id=${bizId}`, { order: nonHome.map((x) => x.page_id) });
      await reloadPages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reorder");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (p) => {
    setEditing(p.page_id);
    setEditForm({
      title: p.title || "",
      slug: p.slug || "",
      nav_label: p.nav_label || "",
      seo_title: p.seo_title || "",
      meta_description: p.meta_description || "",
      hide_from_nav: !!p.hide_from_nav,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const targetPage = pages.find((p) => p.page_id === editing);
      const payload = {
        title: editForm.title,
        nav_label: editForm.nav_label,
        seo_title: editForm.seo_title,
        meta_description: editForm.meta_description,
        hide_from_nav: !!editForm.hide_from_nav,
      };
      if (targetPage && !targetPage.is_home && editForm.slug && editForm.slug !== targetPage.slug) {
        payload.slug = editForm.slug;
      }
      await api.put(`/business/pages/${editing}?business_id=${bizId}`, payload);
      setEditing(null);
      await reloadPages();
      toast.success("Page saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save page");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pages-tab">
      <div className="space-y-2">
        {pages.map((p, i) => {
          const nonHomeList = pages.filter((x) => !x.is_home);
          const nonHomeIdx = nonHomeList.findIndex((x) => x.page_id === p.page_id);
          const canMoveUp = !p.is_home && nonHomeIdx > 0;
          const canMoveDown = !p.is_home && nonHomeIdx >= 0 && nonHomeIdx < nonHomeList.length - 1;
          return (
            <div key={p.page_id} className="border border-gray-200 rounded-md p-3" data-testid={`pages-tab-row-${p.slug || "home"}`}>
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <button onClick={() => movePage(p, -1)} disabled={busy || !canMoveUp}
                    className="p-1 hover:bg-gray-100 rounded disabled:opacity-20"
                    title="Move up" data-testid={`pages-tab-up-${p.slug || "home"}`}>
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => movePage(p, 1)} disabled={busy || !canMoveDown}
                    className="p-1 hover:bg-gray-100 rounded disabled:opacity-20"
                    title="Move down" data-testid={`pages-tab-down-${p.slug || "home"}`}>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">{p.title || p.slug}</span>
                    {p.is_home && <span className="text-[10px] font-black bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">HOME</span>}
                    {p.hide_from_nav && !p.is_home && <span className="text-[10px] font-black bg-gray-200 text-gray-700 rounded px-1.5 py-0.5">HIDDEN</span>}
                  </div>
                  <div className="text-[11px] text-gray-500">/{p.slug || ""}</div>
                </div>
                <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-gray-100 rounded" title="Edit page settings" data-testid={`pages-tab-edit-${p.slug || "home"}`}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => deletePage(p)}
                  disabled={busy || p.is_home}
                  className="p-1.5 hover:bg-red-50 text-red-500 disabled:opacity-30 disabled:hover:bg-transparent rounded"
                  title={p.is_home ? "Home can't be deleted" : "Delete"}
                  data-testid={`pages-tab-delete-${p.slug || "home"}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {editing === p.page_id && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2" data-testid={`pages-tab-edit-form-${p.slug || "home"}`}>
                  <FieldRow label="Page title">
                    <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid={`pages-tab-title-input-${p.slug}`} />
                  </FieldRow>
                  <FieldRow label="Nav label">
                    <Input value={editForm.nav_label} onChange={(e) => setEditForm({ ...editForm, nav_label: e.target.value })} placeholder="(defaults to title)" data-testid={`pages-tab-nav-label-input-${p.slug}`} />
                  </FieldRow>
                  <FieldRow label="URL slug">
                    <Input value={editForm.slug} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })} disabled={p.is_home} placeholder="my-page" data-testid={`pages-tab-slug-input-${p.slug}`} />
                  </FieldRow>
                  <FieldRow label="SEO title">
                    <Input value={editForm.seo_title} onChange={(e) => setEditForm({ ...editForm, seo_title: e.target.value })} placeholder="Custom SEO title (optional)" data-testid={`pages-tab-seo-title-input-${p.slug}`} />
                  </FieldRow>
                  <FieldRow label="Meta description">
                    <Textarea rows={2} value={editForm.meta_description} onChange={(e) => setEditForm({ ...editForm, meta_description: e.target.value })} placeholder="Custom SEO description (optional)" data-testid={`pages-tab-meta-input-${p.slug}`} />
                  </FieldRow>
                  {!p.is_home && (
                    <label className="flex items-center gap-2 text-xs pt-1">
                      <input
                        type="checkbox"
                        checked={!!editForm.hide_from_nav}
                        onChange={(e) => setEditForm({ ...editForm, hide_from_nav: e.target.checked })}
                        data-testid={`pages-tab-hide-input-${p.slug}`}
                      />
                      <span className="text-gray-700 font-medium">Hide from main navigation (still accessible via direct link and footer)</span>
                    </label>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button onClick={saveEdit} disabled={busy} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1" data-testid={`pages-tab-save-${p.slug}`}>
                      Save
                    </Button>
                    <Button onClick={() => setEditing(null)} variant="ghost" size="sm" className="flex-1" data-testid={`pages-tab-cancel-${p.slug}`}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="border-2 border-dashed border-emerald-300 rounded-md p-3 space-y-2">
        <p className="text-xs font-black text-emerald-700 uppercase tracking-wider">Add a new page</p>
        <Input value={addForm.title} onChange={(e) => setAddForm({ title: e.target.value })} placeholder="Page name (e.g. Portfolio)" data-testid="pages-tab-add-title" />
        <p className="text-[11px] text-gray-500">
          The URL slug is generated automatically. The new page uses your active template — body content is edited from the Visual Editor and stored in your website content, not per-page section blocks.
        </p>
        <Button onClick={addPage} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="pages-tab-add-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          <Plus className="w-4 h-4 mr-1" /> Add page
        </Button>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-black uppercase tracking-wider text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function SpinnerBox() {
  return (
    <div className="py-10 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
    </div>
  );
}
